import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckSquare, Warning, ShieldCheck, Flag, UsersThree, Wrench, Globe, Coins,
  ArrowRight, CaretDown, CaretUp,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

const ICON_BY_KEY = {
  pending_approvals: CheckSquare,
  projects_at_risk: Warning,
  compliance_expiring: ShieldCheck,
  overdue_deliverables: Flag,
  payroll_in_flight: UsersThree,
  asset_calibration: Wrench,
  donor_reports: Globe,
  cash_runway: Coins,
};

const TONE_CLS = {
  red:     { ring: "ring-red-200/70",   bg: "bg-gradient-to-br from-red-50 to-white",         val: "text-red-700",     dot: "bg-red-500" },
  amber:   { ring: "ring-amber-200/70", bg: "bg-gradient-to-br from-amber-50 to-white",       val: "text-amber-700",   dot: "bg-amber-500" },
  emerald: { ring: "ring-emerald-200/60", bg: "bg-gradient-to-br from-emerald-50 to-white",   val: "text-emerald-700", dot: "bg-emerald-500" },
  blue:    { ring: "ring-blue-200/70",  bg: "bg-gradient-to-br from-blue-50 to-white",        val: "text-blue-700",    dot: "bg-blue-500" },
};

export default function CommandCenter() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/command-center");
        setData(data);
      } catch { /* silent */ }
    })();
  }, []);

  if (!data) {
    return (
      <div className="dc-card p-5 font-mono-alt text-xs uppercase tracking-[0.3em] text-slate-400" data-testid="cc-loading">
        Loading command center…
      </div>
    );
  }

  const goto = (link) => {
    if (!link) return;
    const lk = link.startsWith("/admin") ? link.slice(6) : link;
    navigate(lk);
  };

  const renderValue = (s) => {
    if (s.is_months) {
      if (s.value == null) return "—";
      return `${s.value} mo`;
    }
    return String(s.value ?? 0);
  };

  return (
    <section className="space-y-3" data-testid="command-center">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-slate-500">Operational Pulse</div>
          <h2 className="font-display text-lg font-semibold text-slate-900">Command Center</h2>
        </div>
        <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-400">as of {data.as_of}</span>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {data.signals.map((s) => {
          const Icon = ICON_BY_KEY[s.key] || CheckSquare;
          const t = TONE_CLS[s.tone] || TONE_CLS.blue;
          const isOpen = expanded === s.key;
          const hasItems = (s.items && s.items.length > 0) || s.breakdown;
          return (
            <div
              key={s.key}
              className={`rounded-xl border border-slate-200 ring-1 ${t.ring} ${t.bg} overflow-hidden transition`}
              data-testid={`cc-signal-${s.key}`}
            >
              <button
                onClick={() => goto(s.link)}
                className="block w-full text-left p-4 hover:bg-white/40 transition"
              >
                <div className="flex items-start justify-between">
                  <Icon size={18} className={t.val} weight="fill" />
                  <span className={`mt-0.5 inline-block h-2 w-2 rounded-full ${t.dot}`} />
                </div>
                <div className={`mt-3 font-display text-3xl font-bold tabular-nums ${t.val}`} data-testid={`cc-value-${s.key}`}>
                  {renderValue(s)}
                </div>
                <div className="mt-1 text-xs font-medium text-slate-700">{s.label}</div>
                {s.secondary && <div className="mt-0.5 text-[11px] text-slate-500">{s.secondary}</div>}
                {s.extra != null && s.key === "compliance_expiring" && s.extra > 0 && (
                  <div className="mt-1 text-[11px] font-semibold text-red-700">+ {s.extra} expired</div>
                )}
              </button>
              {hasItems && (
                <button
                  onClick={() => setExpanded(isOpen ? null : s.key)}
                  className="flex w-full items-center justify-between border-t border-white/60 bg-white/50 px-4 py-1.5 text-[10px] font-mono-alt uppercase tracking-widest text-slate-500 hover:text-slate-700"
                  data-testid={`cc-toggle-${s.key}`}
                >
                  <span>Details</span>
                  {isOpen ? <CaretUp size={10} /> : <CaretDown size={10} />}
                </button>
              )}
              {isOpen && (
                <div className="border-t border-white/60 bg-white px-4 py-3 text-xs space-y-1.5" data-testid={`cc-details-${s.key}`}>
                  {s.breakdown && (
                    <div className="space-y-1">
                      {Object.entries(s.breakdown).map(([k, v]) => (
                        <div key={k} className="flex justify-between">
                          <span className="capitalize text-slate-500">{k.replace(/_/g, " ")}</span>
                          <span className="font-mono-alt tabular-nums">{v}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {(s.items || []).map((it, i) => (
                    <div key={i} className="flex items-start gap-2 border-l-2 border-slate-200 pl-2">
                      <ArrowRight size={9} className="mt-0.5 text-slate-400" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-slate-900">
                          {it.title || it.name || it.staff_name || it.asset_id || it.description}
                        </div>
                        <div className="text-[10px] text-slate-500">
                          {it.expiry_date && `expires ${it.expiry_date}`}
                          {it.due_date && `due ${it.due_date}`}
                          {it.calibration_due && `calibration ${it.calibration_due}`}
                          {it.next_due && `next ${it.next_due}`}
                          {it.period_month && `${it.period_month}`}
                          {it.client && ` · ${it.client}`}
                          {it.project_name && ` · ${it.project_name}`}
                          {(it.overdue_deliverables != null || it.overdue_tasks != null) && (
                            <>
                              {it.overdue_deliverables ? `${it.overdue_deliverables} late deliv` : ""}
                              {it.overdue_deliverables && it.overdue_tasks ? " · " : ""}
                              {it.overdue_tasks ? `${it.overdue_tasks} late tasks` : ""}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
